import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Refresh the Supabase session when the user returns to the tab if the
 * access token is within 1 hour of expiry. This counters the common
 * "I keep getting signed out" complaint on iOS Safari, where a token can
 * silently expire while the tab is backgrounded and Supabase's automatic
 * refresh loop is paused.
 */
export const useKeepSessionAlive = () => {
  useEffect(() => {
    const refreshIfStale = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data.session;
        if (!session) return;
        const expiresAt = (session.expires_at ?? 0) * 1000;
        const msUntilExpiry = expiresAt - Date.now();
        // Refresh proactively if expiring within 1 hour OR already expired.
        if (msUntilExpiry < 60 * 60 * 1000) {
          await supabase.auth.refreshSession();
        }
      } catch {
        // Non-fatal — Supabase will retry on next auth call.
      }
    };

    void refreshIfStale();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refreshIfStale();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refreshIfStale);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refreshIfStale);
    };
  }, []);
};
